/**
 * Journal-entry TIMING estimates ("when did this night start/end"), derived from the entry's wake markers.
 *
 * PURE (no DB/store imports) on purpose: the estimates are needed by BOTH `Server/DB/journalEntries.ts`
 * (stats, session periods, sorting) and the importer's matcher `JournalImport/Match.ts` (whose tests run in
 * the Node lane, so it cannot import the DB module at all). One implementation is what stops the two from
 * drifting -- the matcher compares Discord post dates against these same estimates, so a divergence would
 * silently mis-date every imported dream. Inputs are structural (not the `JournalEntry` class) for the same
 * reason: both it and the importer's `JournalEntryForImport` are assignable.
 *
 * "End of night" prefers the entry's OWN `wakeTime` -- that is the author's explicit statement of when the night
 * ended -- and only falls back to the latest wake-MARKER segment when the entry has none. That order matters: a
 * night can legitimately have a later nap segment added afterwards (wake night at 7am, then nap 1pm-2pm), and
 * that later marker must not move the night's end time.
 */

import type {JournalEntry, JournalSegment} from "../journalEntries/@JournalEntry.js";

/** The parts of a segment this module reads. Only wake markers have a `wakeTime`. */
export type EntryTimeSourceSegment = {
	wakeTime?: number|n;
};

/** The parts of an entry this module reads. */
export type EntryTimeSource = {
	sleepTime?: number|n;
	wakeTime?: number|n;
	createdAt?: number|n;
	segments: ReadonlyArray<EntryTimeSourceSegment|n>;
};

/** Fallback for an entry that only records a `sleepTime`: assume ~8h of sleep to reach the morning. */
export const ASSUMED_SLEEP_DURATION_MS = 8 * 60 * 60 * 1000;

export function EstimateJournalEntryStartTime(dream: JournalEntry): number {
	if (dream.sleepTime) return dream.sleepTime;

	const secondaryTimings = [
		...dream.segments.map(a=>a.wakeTime),
		dream.wakeTime,
	].filter(a=>a != null);
	if (secondaryTimings.length) {
		return secondaryTimings.OrderBy(a=>a)[0]!;
	}

	return dream.createdAt;
}

/** When the night ended: the entry's own `wakeTime` if set (it wins outright, so a nap marker added later cannot
 * extend the night), else the LATEST wake-marker segment's; null if neither exists. For a timestamp regardless
 * (eg. date derivation) use `EstimateDreamEndTime_Fallback` -- this must not invent one for statistics. */
export function EstimateJournalEntryEndTime(entry: EntryTimeSource): number|n {
	if (entry.wakeTime != null) return entry.wakeTime;

	const segmentWakeTimes = entry.segments.map(a=>a?.wakeTime).filter(a=>a != null) as number[];
	if (segmentWakeTimes.length == 0) return null;
	return Math.max(...segmentWakeTimes);
}

/** `EstimateJournalEntryEndTime`, falling back to `sleepTime + ASSUMED_SLEEP_DURATION_MS` and then `createdAt`, so
 * any timing information at all yields a timestamp. Right for DATE derivation (approximate beats none),
 * wrong for duration statistics (a fabricated end time is a lie). */
export function EstimateJournalEntryEndTime_Fallback(entry: EntryTimeSource): number|n {
	const dreamEndTime = EstimateJournalEntryEndTime(entry);
	if (dreamEndTime != null) return dreamEndTime;
	if (entry.sleepTime != null) return entry.sleepTime + ASSUMED_SLEEP_DURATION_MS;
	return entry.createdAt ?? null;
}

export function EstimateSegmentStartTime(dream: JournalEntry, segment: JournalSegment): number {
	const i = dream.segments.indexOf(segment);
	const indexOfFirstSegmentWithWakeTimeSpecified = dream.segments.findIndex(a=>a.wakeTime != null);
	if (i != -1 && i <= indexOfFirstSegmentWithWakeTimeSpecified) return EstimateJournalEntryStartTime(dream);

	const segmentsAfter = dream.segments.slice(dream.segments.indexOf(segment) + 1);
	const firstWakeAfter = segmentsAfter.find(a=>a.wakeTime != null);
	if (firstWakeAfter) return firstWakeAfter.wakeTime!;

	const segmentsBefore = dream.segments.slice(0, dream.segments.indexOf(segment));
	const lastWakeBefore = segmentsBefore.LastOrX(a=>a.wakeTime != null);
	if (lastWakeBefore) return lastWakeBefore.wakeTime!;

	return EstimateJournalEntryStartTime(dream);
}
export function EstimateSegmentEndTime(dream: JournalEntry, segment: JournalSegment): number|n {
	const segmentsAfter = dream.segments.slice(dream.segments.indexOf(segment) + 1);
	const firstWakeAfter = segmentsAfter.find(a=>a.wakeTime != null);
	if (firstWakeAfter) return firstWakeAfter.wakeTime!;

	return EstimateJournalEntryEndTime(dream);
}